import { createSlice, PayloadAction, createAction } from '@reduxjs/toolkit';
import { IState } from 'shared/interfaces/state.interface';
import { TPaginationRequest } from 'shared/types';
import { initialStore } from '../initial-store';

export const createEcommerceOrder = createAction(
  'ecommerceOrder/CREATE_ECOMMERCE_ORDER',
  (payload) => ({ payload })
);

export const updateEcommerceOrder = createAction(
  'ecommerceOrder/UPDATE_ECOMMERCE_ORDER',
  (payload) => ({ payload })
);

export const cancelEcommerceOrder = createAction(
  'ecommerceOrder/CANCEL_ECOMMERCE_ORDER',
  (payload) => ({ payload })
);

export const refundEcommerceOrder = createAction(
  'ecommerceOrder/REFUND_ECOMMERCE_ORDER',
  (payload) => ({ payload })
);

export const validateCoupon = createAction(
  'ecommerceOrder/VALIDATE_COUPON',
  (payload) => ({ payload })
);

export const generateShareLink = createAction(
  'ecommerceOrder/GENERATE_SHARE_LINK',
  (payload) => ({ payload })
);

export const getAvailablePaymentMethods = createAction(
  'ecommerceOrder/GET_AVAILABLE_PAYMENT_METHODS',
  (payload: { patientId: string }) => ({ payload })
);

export const attachPaymentMethod = createAction(
  'ecommerceOrder/ATTACH_PAYMENT_METHOD',
  (payload: {
    patientId: string;
    paymentMethodId: string;
    onSuccess?: (paymentMethodId: string) => void;
    onError?: () => void;
  }) => ({ payload })
);

const ecommerceOrderSlice = createSlice({
  name: 'ecommerceOrder',
  initialState: initialStore.ecommerceOrder,
  reducers: {
    setEcommerceOrderData: (state, { payload }) => {
      state.dataEcommerceOrders = payload || {};
      state.isLoading = false;
    },
    getEcommerceOrderData: (
      state,
      data: PayloadAction<TPaginationRequest | undefined>
    ) => {
      if (!state.isLoading) {
        state.isLoading = true;
      }
    },
    actionFail: (state) => {
      state.isLoading = false;
      state.dataIndividualEcommerceOrder.isLoadingUpdate = false;
    },
    buildEcommerceOrderPage: (state, { payload }) => {
      state.isLoading = true;
    },

    setIndividualEcommerceOrderInfo: (state, { payload }) => {
      const data = payload || {};
      state.dataIndividualEcommerceOrder.data = {
        ...data
      };
      state.isLoading = false;
    },
    setEcommerceOrder: (state, { payload }) => {
      state.dataIndividualEcommerceOrder.data = {
        ...payload
      };
      state.isLoading = false;
    },
    createEcommerceOrderSuccess: (state) => {
      state.isLoading = false;
    },
    updateEcommerceOrderSuccess: (state) => {
      state.isLoading = false;
    },
    cancelEcommerceOrderSuccess: (state) => {
      state.isLoading = false;
    },
    refundEcommerceOrderSuccess: (state) => {
      state.isLoading = false;
    },
    setCouponValidationResult: (state, { payload }) => {
      state.couponValidation = payload;
      state.isCouponLoading = false;
    },
    setCouponValidationError: (state, { payload }) => {
      state.couponValidation = null;
      state.couponError = payload;
      state.isCouponLoading = false;
    },
    clearCouponValidation: (state) => {
      state.couponValidation = null;
      state.couponError = null;
      state.isCouponLoading = false;
    },
    setShareLinkSuccess: (state, { payload }) => {
      state.shareLinks = {
        ...state.shareLinks,
        [payload.recordId]: payload.url
      };
    },
    setAvailablePaymentMethods: (state, { payload }) => {
      state.availablePaymentMethods = payload || [];
      state.isLoadingPaymentMethods = false;
    },
    clearAvailablePaymentMethods: (state) => {
      state.availablePaymentMethods = [];
      state.isLoadingPaymentMethods = false;
    },
    setAvailablePaymentMethodsLoading: (state) => {
      state.isLoadingPaymentMethods = true;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(createEcommerceOrder, (state) => {
        state.isLoading = true;
      })
      .addCase(updateEcommerceOrder, (state) => {
        state.isLoading = true;
      })
      .addCase(cancelEcommerceOrder, (state) => {
        state.isLoading = true;
      })
      .addCase(refundEcommerceOrder, (state) => {
        state.isLoading = true;
      })
      .addCase(validateCoupon, (state) => {
        state.isCouponLoading = true;
        state.couponError = null;
      })
      .addCase(generateShareLink, (state) => {
        state.isCouponLoading = true;
      })
      .addCase(getAvailablePaymentMethods, (state) => {
        state.isLoadingPaymentMethods = true;
      })
      .addCase(attachPaymentMethod, (state) => {
        state.isLoadingPaymentMethods = true;
      });
  }
});

export const {
  setEcommerceOrderData,
  getEcommerceOrderData,
  actionFail,
  buildEcommerceOrderPage,
  setIndividualEcommerceOrderInfo,
  setEcommerceOrder,
  createEcommerceOrderSuccess,
  updateEcommerceOrderSuccess,
  cancelEcommerceOrderSuccess,
  refundEcommerceOrderSuccess,
  setCouponValidationResult,
  setCouponValidationError,
  clearCouponValidation,
  setShareLinkSuccess,
  setAvailablePaymentMethods,
  clearAvailablePaymentMethods,
  setAvailablePaymentMethodsLoading
} = ecommerceOrderSlice.actions;

export const { reducer } = ecommerceOrderSlice;

// Selectors
export const getEcommerceOrdersSelector = (state: IState) =>
  state && state?.ecommerceOrder?.dataEcommerceOrders;

export const getEcommerceOrdersLoadingSelector = (state: IState) =>
  state && state?.ecommerceOrder?.isLoading;

export const getEcommerceOrdersLoadingUpdateSelector = (state: IState) =>
  state && state?.ecommerceOrder?.dataIndividualEcommerceOrder?.isLoadingUpdate;

export const getEcommerceOrderIndividualSelector = (state: IState) =>
  state && {
    data: state.ecommerceOrder.dataIndividualEcommerceOrder?.data
  };

export const getEcommerceOrderCount = (state: IState) =>
  state && state?.ecommerceOrder?.dataEcommerceOrders?.count;

export const getCouponValidationSelector = (state: IState) =>
  state?.ecommerceOrder?.couponValidation;

export const getCouponErrorSelector = (state: IState) =>
  state && state?.ecommerceOrder?.couponError;

export const getIsCouponLoadingSelector = (state: IState) =>
  state && state?.ecommerceOrder?.isCouponLoading;

export const getShareLinksSelector = (state: IState) =>
  state && state?.ecommerceOrder?.shareLinks;

export const getAvailablePaymentMethodsSelector = (state: IState) =>
  (state?.ecommerceOrder?.availablePaymentMethods as any)?.data
    ?.paymentMethods || [];

export const getIsLoadingPaymentMethodsSelector = (state: IState) =>
  state?.ecommerceOrder?.isLoadingPaymentMethods || false;
